import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { DropdownDescriptiveItem, type DropdownDescriptiveItemProps } from './DropdownDescriptiveItem';
import { InputSingleSelect } from '../Input/InputSingleSelect';
import { useControllableState } from '../../hooks/useControllableState';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useListNavigation } from '../../hooks/useListNavigation';
import { getOverlayPortalParent } from '../../utils/getOverlayPortalParent';
import type { DropdownDescriptiveMenuProps } from './DropdownDescriptiveMenu.types';
import './DropdownDescriptiveMenu.css';

/** Gap between the trigger and the floating panel (Figma overlay spacing = 4px). */
const PANEL_GAP = 4;
const MAX_VISIBLE_ITEMS = 4;
const MENU_ID_PREFIX = 'spark-dropdown-descriptive-item-';

export function DropdownDescriptiveMenu({
  open,
  defaultOpen = false,
  onOpenChange,
  closeOnOutsideClick = true,
  panelLayout = 'overlay',
  size = 'medium',
  placeholder = 'Input',
  showPrefixIcon = false,
  disabled = false,
  items = [],
  value,
  onSelect,
  className = '',
  panelClassName = '',
  visualState,
  showFocus,
}: DropdownDescriptiveMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useControllableState(open, defaultOpen, onOpenChange);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

  const close = useCallback(() => setOpen(false), [setOpen]);

  const isPortaled = panelLayout === 'overlay';

  const { activeIndex, handleKeyDown } = useListNavigation({
    itemCount: items.length,
  });

  const updatePosition = useCallback(() => {
    const trigger = rootRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const panel = panelRef.current;
    const panelHeight = panel?.offsetHeight ?? 0;
    const panelWidth = panel?.offsetWidth ?? rect.width;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < panelHeight + PANEL_GAP && spaceAbove > spaceBelow;
    const top = openUp
      ? Math.max(PANEL_GAP, rect.top - PANEL_GAP - panelHeight)
      : rect.bottom + PANEL_GAP;

    let left = rect.left;
    const rightOverflow = left + panelWidth - (viewportWidth - PANEL_GAP);
    if (rightOverflow > 0) left = Math.max(PANEL_GAP, left - rightOverflow);

    setPanelStyle({
      top,
      left,
      '--spark-dropdown-descriptive-trigger-width': `${rect.width}px`,
    } as CSSProperties);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !isPortaled) return undefined;
    updatePosition();
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
  }, [isOpen, isPortaled, size, updatePosition]);

  useEffect(() => {
    if (!isOpen || !isPortaled) return undefined;
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, isPortaled, updatePosition]);

  useOutsideClick([rootRef, panelRef], close, isOpen && closeOnOutsideClick);
  useEscapeKey(close, isOpen);

  const triggerText = typeof value === 'string' ? value : undefined;

  const rootClasses = [
    'spark-dropdown-descriptive-menu',
    isOpen ? 'spark-dropdown-descriptive-menu-open' : 'spark-dropdown-descriptive-menu-closed',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleToggle = () => {
    if (!disabled) setOpen(!isOpen);
  };

  const handleSelect = (item: DropdownDescriptiveItemProps, index: number) => {
    if (item.state === 'disabled' || item.visualState === 'disabled') return;
    onSelect?.(item, index);
    // Single-select: the portaled panel is inside the outside-click refs, so a
    // click on an option is not an outside click and will not close the menu.
    close();
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    handleKeyDown(e);
    if ((e.key === 'Enter' || e.key === ' ') && activeIndex >= 0 && activeIndex < items.length) {
      e.preventDefault();
      handleSelect(items[activeIndex], activeIndex);
    }
  };

  const isScrollable = items.length > MAX_VISIBLE_ITEMS;

  const panelClasses = [
    isPortaled ? 'spark-dropdown-descriptive-menu-portal-panel' : 'spark-dropdown-descriptive-menu-inline-panel',
    panelClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const panel = (
    <div ref={panelRef} className={panelClasses} style={isPortaled ? panelStyle : undefined}>
      <div
        className={[
          'spark-dropdown-descriptive-panel',
          isScrollable ? 'spark-dropdown-descriptive-scrollable' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="listbox"
        onKeyDown={handleMenuKeyDown}
        aria-activedescendant={activeIndex >= 0 ? `${MENU_ID_PREFIX}${activeIndex}` : undefined}
      >
        <div className="spark-dropdown-descriptive-body">
          {items.map((item, index) => {
            const isSelectedItem = value !== undefined && item.title === value;
            const resolvedState = item.state ?? (isSelectedItem ? 'selected' : 'default');
            const resolvedVisual = index === activeIndex && !item.visualState ? 'hover' : item.visualState;

            return (
              <DropdownDescriptiveItem
                key={`${item.title}-${index}`}
                {...item}
                id={`${MENU_ID_PREFIX}${index}`}
                state={resolvedState}
                visualState={resolvedVisual}
                showPrefixIcon={item.showPrefixIcon ?? showPrefixIcon}
                onClick={() => {
                  item.onClick?.();
                  handleSelect(item, index);
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className={rootClasses} ref={rootRef}>
      <InputSingleSelect
        size={size}
        placeholder={placeholder}
        text={triggerText}
        showLabel={false}
        showPrefixIcon={showPrefixIcon}
        disabled={disabled}
        open={isOpen}
        onToggle={handleToggle}
        visualState={visualState}
        showFocus={showFocus}
        fill={triggerText !== undefined && triggerText.length > 0 ? 'filled' : 'empty'}
      />
      {isOpen ? (isPortaled ? createPortal(panel, getOverlayPortalParent(rootRef.current)) : panel) : null}
    </div>
  );
}
